import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, RotateCcw } from "lucide-react";
import { QUIZ_MAP } from "../../data/content";
import { scrollToId } from "../../lib/scroll";
import { useLang, fill } from "../../i18n/LanguageContext";

const scoreQuiz = (answers) => {
  const tally = {};
  answers.forEach((a) => {
    const key = QUIZ_MAP[a];
    if (key) tally[key] = (tally[key] || 0) + 1;
  });
  const keys = Object.keys(tally);
  if (!keys.length) return "singing";
  return keys.reduce((best, k) => (tally[k] > (tally[best] || 0) ? k : best), keys[0]);
};

export const TalentQuiz = () => {
  const { t } = useLang();
  const s = t.games.quiz;
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  const choose = (value) => {
    const next = [...answers, value];
    setAnswers(next);
    if (step < s.questions.length - 1) {
      setStep(step + 1);
    } else {
      setResult(scoreQuiz(next));
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers([]);
    setResult(null);
  };

  const progress = (step / s.questions.length) * 100;
  const talent = result ? s.talents[result] : null;

  return (
    <div data-testid="game-quiz" className="min-h-[320px]">
      {!result && (
        <>
          <div className="mb-6 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
            <motion.div className="h-full bg-gradient-to-r from-gold-light to-gold-primary" animate={{ width: `${progress}%` }} />
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}>
              <p className="mb-1 text-xs uppercase tracking-[0.2em] text-gold-light">{s.question} {step + 1} {s.of} {s.questions.length}</p>
              <h4 className="mb-6 font-display text-2xl text-white md:text-3xl">{s.questions[step].q}</h4>
              <div className="grid gap-3">
                {s.questions[step].options.map((o, i) => (
                  <button
                    key={i}
                    onClick={() => choose(o.value)}
                    data-testid={`quiz-option-${step}-${i}`}
                    className="rounded-xl border border-white/12 bg-white/5 px-5 py-4 text-left text-white/85 transition-all hover:-translate-y-0.5 hover:border-gold-light/50 hover:bg-white/10"
                  >
                    {o.text}
                  </button>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </>
      )}

      {result && (
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center py-6 text-center">
          <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-gold-light to-gold-primary text-ink-900">
            <Sparkles className="h-8 w-8" />
          </span>
          <p className="text-xs uppercase tracking-[0.2em] text-gold-light">{s.yourTalent}</p>
          <h3 className="mt-2 font-display text-4xl text-gold-gradient" data-testid="quiz-result">{talent.name}</h3>
          <p className="mt-4 max-w-sm text-white/70">{talent.blurb}</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <button onClick={reset} data-testid="quiz-retry" className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm text-white/80 hover:text-gold-light">
              <RotateCcw className="h-4 w-4" /> {s.tryAgain}
            </button>
            <a href="#register" onClick={(e) => { e.preventDefault(); scrollToId("#register"); }} className="rounded-full bg-gradient-to-r from-gold-light to-gold-primary px-6 py-3 text-sm font-medium text-ink-900">
              {fill(s.registerFor, { t: talent.name })}
            </a>
          </div>
        </motion.div>
      )}
    </div>
  );
};
